import axios from 'axios';
import {
  isOAuthAuthHost,
  isProductionMultiHostAuth,
  isDistrictSubdomainHost
} from './authOrigin';

const LOGIN_LIKE_PATHS = ['/login', '/register', '/complete-district-signup', '/google-link'];

function defaultPathForRole(role) {
  if (role === 'superadmin') return '/superadmin';
  if (role === 'district_admin' || role === 'district_editor' || role === 'district_user') {
    return '/district-admin';
  }
  if (role === 'parent') {
    if (typeof window !== 'undefined' && localStorage.getItem('gettingStartedDismissed') === 'true') {
      return '/parent/student-intake';
    }
    return '/parent/getting-started';
  }
  return '/';
}

function navigateByRole(navigate, role) {
  navigate(defaultPathForRole(role), { replace: true });
}

function resolveNextPathAfterAuth(returnUrlString, role) {
  try {
    const u = new URL(returnUrlString);
    const path = u.pathname;
    if (LOGIN_LIKE_PATHS.includes(path)) {
      return defaultPathForRole(role);
    }
    if (path === '/' && !u.search) {
      return defaultPathForRole(role);
    }
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return defaultPathForRole(role);
  }
}

function isAllowedHandoffOrigin(originUrl) {
  try {
    const u = new URL(originUrl);
    const host = u.hostname.toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1') {
      return u.protocol === 'http:' || u.protocol === 'https:';
    }
    if (host === 'backtoschoolforms.com' || host === 'www.backtoschoolforms.com') {
      return u.protocol === 'https:';
    }
    if (/^[a-z0-9-]+\.backtoschoolforms\.com$/i.test(host)) {
      return u.protocol === 'https:';
    }
    return false;
  } catch {
    return false;
  }
}

function isSafeNextPath(p) {
  if (!p || typeof p !== 'string') return false;
  if (!p.startsWith('/')) return false;
  if (p.startsWith('//')) return false;
  if (p.includes('..')) return false;
  return true;
}

/**
 * After successful login/register on the auth origin, send the user to the right host (subdomain) with a one-time code.
 */
export async function finishAuthAndRedirect(navigate, userData) {
  const role = userData?.role;
  if (!role) {
    navigate('/', { replace: true });
    return;
  }

  if (!isProductionMultiHostAuth()) {
    navigateByRole(navigate, role);
    return;
  }

  const onAuthHost = isOAuthAuthHost();
  let returnTo = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('auth_return_to') : null;
  if (returnTo) {
    sessionStorage.removeItem('auth_return_to');
  }
  if (returnTo && !isAllowedHandoffOrigin(returnTo)) {
    returnTo = null;
  }

  const meRes = await axios.get('/api/auth/me').catch(() => null);
  const me = meRes?.data;
  const districtSubdomain = me?.subdomain;

  const needsSubdomain =
    (role === 'parent' || role === 'district_admin' || role === 'district_editor' || role === 'district_user') &&
    districtSubdomain;

  if (onAuthHost && needsSubdomain) {
    const expectedHost = `${String(districtSubdomain).toLowerCase()}.backtoschoolforms.com`;
    let targetOrigin = `https://${expectedHost}`;
    let nextPath = defaultPathForRole(role);

    if (returnTo) {
      try {
        const ru = new URL(returnTo);
        if (isDistrictSubdomainHost(ru.hostname) && ru.hostname.toLowerCase() === expectedHost) {
          targetOrigin = ru.origin;
          nextPath = resolveNextPathAfterAuth(returnTo, role);
        }
      } catch {
        /* use defaults */
      }
    }

    const pathOnly = nextPath.split('?')[0];
    if (!isSafeNextPath(pathOnly)) {
      nextPath = defaultPathForRole(role);
    }

    if (targetOrigin === `${window.location.protocol}//${window.location.host}`) {
      navigateByRole(navigate, role);
      return;
    }

    const { data } = await axios.post('/api/auth/handoff-session');
    const code = data?.code;
    if (!code) {
      navigateByRole(navigate, role);
      return;
    }

    window.location.href = `${targetOrigin}/auth/import?code=${encodeURIComponent(code)}&next=${encodeURIComponent(nextPath)}`;
    return;
  }

  if (onAuthHost) {
    if (returnTo) {
      try {
        const ru = new URL(returnTo);
        if (ru.origin === `${window.location.protocol}//${window.location.host}`) {
          const np = resolveNextPathAfterAuth(returnTo, role);
          const basePath = np.split('?')[0];
          if (isSafeNextPath(basePath)) {
            navigate(np, { replace: true });
            return;
          }
        }
      } catch {
        /* fall through */
      }
    }
    navigateByRole(navigate, role);
    return;
  }

  navigateByRole(navigate, role);
}
